import * as React from 'react';
import {useEffect, useState} from 'react';

import {IContentBlock} from 'features/blocks/types';
import {SearchService} from 'features/search/services/SearchService';
import {ObjectStatus} from 'features/search/filters/enums';
import {EpiImage, SearchResult} from "features/search/types";
import { ImageSizes } from '@organisms/Shell/image-size-enum';

import 'components/blocks/ProjectAndObject/SelectedObjectsBlock/style.scss';
import '@molecules/ResidentialCards/ResidenceCardsOld/style.scss';
import { MainGrid } from '@atoms/Layout/MainGrid';
import { AsyncImage } from '@atoms/Media/AsyncImage';
import {AccessibleSlider} from '@molecules/SlidingContent/AccessibleSlider';
import { A } from '@atoms/LinksAndButtons/A';
import { Icon } from '@atoms/Media/Icon';
import { getLocalizedKvm, getLocalizedPrice, getLocalizedRok } from 'features/services/utils/env-helper';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import { ResidenceCard, ResidenceCardType } from '@molecules/ResidentialCards/ResidenceCard';
import { LinkStyle, HeadlineLevel} from 'features/ui/enums';
import {Headline} from '@atoms/Text/Headline';

interface SelectedObjectsBlockType {
    selectedItems: IContentBlock<ExternalObject>[];
    header: string;
    intro: string;
    buttonText: string;
    buttonLink: string;
}

interface SelectedRenderItems { [key: string] : SearchResult[] }

type Props = IContentBlock<SelectedObjectsBlockType>;

interface ExternalObject {
    objectId?: string;
    areaMunicipalityText: string;
    areaText: string;
    numberOfRoomsText: string;
    objectImage: EpiImage;
    objectRealEstateLink: string;
    priceText: string
}

export const SelectedObjectsBlock: React.FunctionComponent<Props> = (props) => {
    const [renderItems, setRenderItems] = useState<SelectedRenderItems>({} as SelectedRenderItems);
    const [externalItems, setExternalItems] =  useState<ExternalObject[]>([]);

    useEffect(() => {
        fetchObjects();
    }, []);

    const fetchObjects = async () => {
        if(props.data.selectedItems.length === 0) return;

        const ids = [] as string[];
        const renderItems = {} as SelectedRenderItems;
        const items = [] as ExternalObject[];

        props.data.selectedItems.map((selectedItem) => {
            if(selectedItem.data.objectId !== undefined) { 
                ids.push(selectedItem.data.objectId)
            } else {
                items.push(selectedItem.data);
            }
        })

        setExternalItems(items);

        const response = await SearchService.searchByIdsObject(ids);

        props.data.selectedItems.map((selectedItem) => {
            if(!selectedItem.data.objectId) return;

            const selectedIds = [] as string[];

            renderItems[selectedItem.data.objectId] = [];

            selectedIds.push(selectedItem.data.objectId);

            selectedIds.map((id) => {
                const hit = response.data?.hits.find(x => x.id === id);

                if(hit) {
                    if(hit.objectStatus === ObjectStatus.FOR_SALE){
                        if(!selectedItem.data.objectId) return;
                        renderItems[selectedItem.data.objectId].push(hit);
                    }
                }
            })
        });

        setRenderItems(renderItems);
    };
    const getResidenceObjects = () => {
        let residenceObjects = [] as SearchResult[];

        Object.keys(renderItems).map((x) => {
            residenceObjects = residenceObjects.concat(renderItems[x]);
        });

        return residenceObjects;
    };

    if(externalItems.length > 0 && getResidenceObjects().length === 0) return (
        <div className='selected-objects-block'>
            <Headline level={HeadlineLevel.H3}>{props.data.header}</Headline>
            <MainGrid>
                {externalItems.map((item) => (
                    item.objectRealEstateLink 
                    ? <div className="residence-block external-item-block-card">
                        <div className="residence-block__content">
                            {item.objectImage && 
                                <div className="residence-block__image">
                                    <AsyncImage
                                        image={item.objectImage}
                                        quality={ImageSizes.SIZE_400}
                                        fullHeight
                                    />
                                </div>
                            }
            
                            <div className="residence-block__text">
                                {item.areaMunicipalityText && 
                                    <span className="succession-block-card__location">
                                        {item.areaMunicipalityText}
                                    </span>
                                }

                                {(item.numberOfRoomsText || item.areaText) && 
                                    <Headline level={HeadlineLevel.H4}>
                                        {item.numberOfRoomsText && getLocalizedRok(item.numberOfRoomsText)}
                                        {(item.numberOfRoomsText && item.areaText) && <>, </>}
                                        {item.areaText && getLocalizedKvm(item.areaText)}
                                    </Headline>
                                }
            
                                {item.priceText && <span className="succession-block-card__price">{getLocalizedPrice(item.priceText)}</span>}
            
                                <span className="succession-block-card__link" aria-hidden="true">
                                    {getFormattedMessage('jm.components.SuccessionStageCard.goToAgent')} <Icon name="external-link" size={20} />
                                </span>
                            </div>
                        </div>
                        <A 
                            href={item.objectRealEstateLink} 
                            target="_blank"
                            variant={LinkStyle.UNSTYLED}
                            className='residence-block__link'
                            title={getFormattedMessage('jm.components.SuccessionStageCard.goToAgent')}
                        >
                            <span className='visually-hidden'>
                                {getFormattedMessage('jm.components.SuccessionStageCard.goToAgent')}
                            </span>
                        </A>
                    </div>
                    : <></>
                ))}
            </MainGrid>
        </div>
    );

    return (
        <AccessibleSlider
            className='selected-objects-block'
            id={props.id}
            header={props.data.header}
            textHtml={props.data.intro}
            link={props.data.buttonLink}
            linkText={props.data.buttonText}
        >
            {getResidenceObjects().slice(0, 10).map((residenceObject, _id) => (
                <ResidenceCard
                    residence={residenceObject} 
                    key={`card-${residenceObject.id}`}  
                    cardType={ResidenceCardType.OBJECT} 
                    horizontal={getResidenceObjects().length === 1}
                />
            ))}
        </AccessibleSlider>
    );
};